import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  Button,
  CircularProgress,
  Collapse,
} from "@mui/material";
import {
  CheckCircle as ConfirmIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Science as ExtractIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import {
  useGetFirstSaleDataExtractionQuery,
  useRunFirstSaleDataExtractionMutation,
  useUpdateDataExtractionComponentMutation,
  useAddDataExtractionComponentMutation,
  useRemoveDataExtractionComponentMutation,
} from "@/services/first-sale";

interface DataExtractionPanelProps {
  caseId: string;
  tenantId?: string;
  materialsProvided?: boolean;
  middlemanValue?: number;
  dutyRate?: number;
  htsCode?: string;
}

const CONFIDENCE_COLORS: Record<string, "success" | "warning" | "error"> = {
  HIGH: "success",
  MEDIUM: "warning",
  LOW: "error",
};

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Savings Derivation Table ─────────────────────────────────────

type SavingsRowId = "first_sale_per_unit" | "transaction_per_unit" | "total_units" | "first_sale_total" | "transaction_total" | "dutiable_reduction" | "duty_rate" | "duty_savings";
type SavingsRowStatus = "pending" | "confirmed" | "rejected" | "edited";

interface SavingsDerivationProps {
  summary: any;
  middlemanValue?: number;
  dutyRate?: number;
  htsCode?: string;
}

const SavingsDerivationTable: React.FC<SavingsDerivationProps> = ({
  summary,
  middlemanValue,
  dutyRate,
  htsCode,
}) => {
  const [editingRow, setEditingRow] = useState<SavingsRowId | null>(null);
  const [editValue, setEditValue] = useState("");
  const [overrides, setOverrides] = useState<Partial<Record<SavingsRowId, number>>>({});
  const [statuses, setStatuses] = useState<Partial<Record<SavingsRowId, SavingsRowStatus>>>({});

  if (!summary) return null;

  // Source values (extraction or case), with overrides
  const firstSalePerUnit = overrides.first_sale_per_unit ?? summary.firstSalePrice;
  const totalUnits = overrides.total_units ?? (summary.totalUnits || 0);
  const txnPerUnit = overrides.transaction_per_unit
    ?? (totalUnits > 0 && middlemanValue != null && middlemanValue > 0
      ? Math.round((middlemanValue / totalUnits) * 100) / 100
      : 0);
  const currency = summary.currency || "USD";

  const hasUnits = totalUnits > 0;
  const hasMV = middlemanValue != null && middlemanValue > 0;
  const hasDutyRate = dutyRate != null && dutyRate > 0;

  // Computed values
  const firstSaleTotal = hasUnits ? Math.round(firstSalePerUnit * totalUnits * 100) / 100 : 0;
  const txnTotal = hasUnits && txnPerUnit > 0 ? Math.round(txnPerUnit * totalUnits * 100) / 100 : (hasMV ? middlemanValue! : 0);
  const dutiableReduction = txnTotal > firstSaleTotal ? Math.round((txnTotal - firstSaleTotal) * 100) / 100 : 0;
  const dutySavings = hasDutyRate && dutiableReduction > 0
    ? Math.round(dutiableReduction * (dutyRate! / 100) * 100) / 100
    : 0;

  const handleStatus = (id: SavingsRowId, status: SavingsRowStatus) => {
    setStatuses({ ...statuses, [id]: status });
  };

  const handleStartEdit = (id: SavingsRowId, currentValue: number) => {
    setEditingRow(id);
    setEditValue(String(currentValue));
  };

  const handleSaveEdit = (id: SavingsRowId) => {
    const val = parseFloat(editValue);
    if (!isNaN(val) && val >= 0) {
      setOverrides({ ...overrides, [id]: val });
      setStatuses({ ...statuses, [id]: "edited" });
    }
    setEditingRow(null);
  };

  type Row = {
    id: SavingsRowId;
    metric: string;
    value: number;
    display: string;
    source: string;
    editable: boolean;
    separator?: boolean;
  };

  const rows: Row[] = [];

  rows.push({
    id: "first_sale_per_unit",
    metric: "First Sale Price (per unit)",
    value: firstSalePerUnit,
    display: `${fmt(firstSalePerUnit)} ${currency}`,
    source: "Cost Breakdown Sheet",
    editable: true,
  });

  rows.push({
    id: "transaction_per_unit",
    metric: "Transaction Value (per unit)",
    value: txnPerUnit,
    display: txnPerUnit > 0 ? `${fmt(txnPerUnit)} ${currency}` : "\u2014",
    source: hasMV && hasUnits ? `$${fmt(middlemanValue!)} \u00F7 ${totalUnits} units` : hasMV ? "Case middleman value" : "Set middleman value on case",
    editable: true,
  });

  rows.push({
    id: "total_units",
    metric: "Total Units",
    value: totalUnits,
    display: hasUnits ? totalUnits.toLocaleString() : "\u2014",
    source: hasUnits ? "Middleman Invoice (extracted)" : "Not yet extracted",
    editable: true,
  });

  rows.push({
    id: "first_sale_total",
    metric: "First Sale Total",
    value: firstSaleTotal,
    display: firstSaleTotal > 0 ? `$${fmt(firstSaleTotal)}` : "\u2014",
    source: hasUnits ? `${fmt(firstSalePerUnit)} \u00D7 ${totalUnits}` : "Needs total units",
    editable: false,
    separator: true,
  });

  rows.push({
    id: "transaction_total",
    metric: "Transaction Value Total",
    value: txnTotal,
    display: txnTotal > 0 ? `$${fmt(txnTotal)}` : "\u2014",
    source: hasUnits && txnPerUnit > 0 ? `${fmt(txnPerUnit)} \u00D7 ${totalUnits}` : hasMV ? "Case middleman value" : "Set middleman value on case",
    editable: false,
  });

  rows.push({
    id: "dutiable_reduction",
    metric: "Dutiable Value Reduction",
    value: dutiableReduction,
    display: txnTotal > 0 && firstSaleTotal > 0
      ? (dutiableReduction > 0 ? `$${fmt(dutiableReduction)}` : "$0.00")
      : "\u2014",
    source: txnTotal > 0 && firstSaleTotal > 0
      ? `$${fmt(txnTotal)} \u2212 $${fmt(firstSaleTotal)}`
      : "Needs both totals above",
    editable: false,
    separator: true,
  });

  rows.push({
    id: "duty_rate",
    metric: `Duty Rate${htsCode ? ` (${htsCode})` : ""}`,
    value: dutyRate ?? 0,
    display: hasDutyRate ? `${dutyRate}%` : "\u2014",
    source: hasDutyRate ? "Case settings" : "Set duty rate on case",
    editable: false,
  });

  rows.push({
    id: "duty_savings",
    metric: "Estimated Duty Savings",
    value: dutySavings,
    display: hasDutyRate && txnTotal > 0 && firstSaleTotal > 0
      ? `$${fmt(dutySavings)}`
      : "\u2014",
    source: hasDutyRate && dutiableReduction > 0
      ? `$${fmt(dutiableReduction)} \u00D7 ${dutyRate}%`
      : "Needs dutiable reduction and duty rate",
    editable: false,
  });

  const confirmedCount = Object.values(statuses).filter((s) => s === "confirmed").length;
  const pendingCount = rows.length - Object.keys(statuses).length;

  return (
    <Box sx={{ borderTop: "2px solid #e2e5e9" }}>
      <Box sx={{ px: 2, py: 1, bgcolor: "#f8f9fa", borderBottom: "1px solid #e2e5e9" }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 12 }}>
            First Sale Savings
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {confirmedCount > 0 && (
              <Chip label={`${confirmedCount} confirmed`} size="small" color="success" variant="outlined" sx={{ height: 20 }} />
            )}
            {pendingCount > 0 && (
              <Chip label={`${pendingCount} pending`} size="small" variant="outlined" sx={{ height: 20 }} />
            )}
          </Stack>
        </Stack>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Metric</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">Value</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isEditing = editingRow === row.id;
              const status = statuses[row.id];
              const isRejected = status === "rejected";
              const isFinal = row.id === "duty_savings";

              return (
                <TableRow
                  key={row.id}
                  sx={{
                    opacity: isRejected ? 0.5 : 1,
                    bgcolor: isFinal ? "#e8f5e9" : undefined,
                    borderTop: row.separator ? "2px solid #e2e5e9" : undefined,
                    "&:hover": { bgcolor: isFinal ? "#c8e6c9" : "#f5f5f5" },
                  }}
                >
                  <TableCell sx={{ fontSize: 11, fontWeight: isFinal ? 700 : 400 }}>
                    {row.metric}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: isFinal ? 700 : 600, fontVariantNumeric: "tabular-nums", color: isFinal ? "success.dark" : undefined }}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(row.id); if (e.key === "Escape") setEditingRow(null); }}
                        sx={{ width: 90, "& input": { fontSize: 11, py: 0.5, textAlign: "right" } }}
                      />
                    ) : (
                      row.display
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 10, color: tokens.text.muted }}>
                    {row.source}
                    {status === "edited" && (
                      <Chip label="edited" size="small" variant="outlined" sx={{ ml: 0.5, height: 14, fontSize: 8 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {status ? (
                      <Chip
                        label={status}
                        size="small"
                        color={status === "confirmed" ? "success" : status === "rejected" ? "error" : status === "edited" ? "warning" : "default"}
                        variant="outlined"
                        sx={{ height: 18, fontSize: 9 }}
                      />
                    ) : (
                      <Chip label="pending" size="small" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isEditing ? (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Button size="small" onClick={() => handleSaveEdit(row.id)} sx={{ fontSize: 10, minWidth: 0, textTransform: "none" }}>
                          Save
                        </Button>
                        <Button size="small" onClick={() => setEditingRow(null)} sx={{ fontSize: 10, minWidth: 0, textTransform: "none" }}>
                          Cancel
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0} justifyContent="center">
                        <Tooltip title="Confirm">
                          <IconButton size="small" onClick={() => handleStatus(row.id, "confirmed")} sx={{ color: "success.main" }}>
                            <ConfirmIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small" onClick={() => handleStatus(row.id, "rejected")} sx={{ color: "error.main" }}>
                            <RejectIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        {row.editable && (
                          <Tooltip title="Override">
                            <IconButton size="small" onClick={() => handleStartEdit(row.id, row.value)}>
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {dutiableReduction === 0 && txnTotal > 0 && firstSaleTotal > 0 && (
        <Box sx={{ px: 2, py: 1, bgcolor: "#fff8e1", borderTop: "1px solid #e2e5e9" }}>
          <Typography variant="caption" color="warning.dark" sx={{ fontSize: 10 }}>
            Transaction value equals first sale total — no savings on this case. Verify the middleman value is the price charged to the importer, not the factory price.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const POLL_INTERVAL_MS = 2000;

export const DataExtractionPanel: React.FC<DataExtractionPanelProps> = ({
  caseId,
  tenantId,
  materialsProvided,
  middlemanValue,
  dutyRate,
  htsCode,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState({ supplierName: "", materialDescription: "", perUnitCostUSD: "" });

  const tVars = tenantId ? { tenantId } : {};

  const [polling, setPolling] = useState(false);

  const { data: extraction } = useGetFirstSaleDataExtractionQuery(
    { caseId, ...tVars },
    {
      pollingInterval: polling ? POLL_INTERVAL_MS : 0,
      skip: !materialsProvided,
    }
  );

  // Start/stop polling based on extraction status
  React.useEffect(() => {
    if (extraction?.status === "EXTRACTING") {
      setPolling(true);
    } else if (polling && extraction?.status !== "EXTRACTING") {
      setPolling(false);
    }
  }, [extraction?.status, polling]);

  const [runExtraction, { isLoading: extracting }] = useRunFirstSaleDataExtractionMutation();
  const [updateComponent] = useUpdateDataExtractionComponentMutation();
  const [addComponent] = useAddDataExtractionComponentMutation();
  const [removeComponent] = useRemoveDataExtractionComponentMutation();

  const isExtracting = extraction?.status === "EXTRACTING" || extracting;
  const components = extraction?.components || [];
  const summary = extraction?.summary;

  const confirmedCount = components.filter((c: any) => c.userStatus === "confirmed").length;
  const rejectedCount = components.filter((c: any) => c.userStatus === "rejected").length;
  const pendingCount = components.filter((c: any) => c.userStatus === "pending").length;

  const handleRunExtraction = async () => {
    try {
      await runExtraction({ caseId, ...tVars }).unwrap();
    } catch (err) {
      console.error("Extraction failed:", err);
    }
  };

  const handleStatusUpdate = async (componentId: string, userStatus: string) => {
    try {
      await updateComponent({
        caseId,
        componentId,
        input: { userStatus },
        ...tVars,
      }).unwrap();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleStartEdit = (comp: any) => {
    setEditingId(comp.componentId);
    setEditValues({
      supplierName: comp.supplierName,
      materialDescription: comp.materialDescription,
      quantity: comp.quantity ?? "",
      perUnitCostUSD: comp.perUnitCostUSD,
    });
  };

  const handleSaveEdit = async (componentId: string) => {
    try {
      await updateComponent({
        caseId,
        componentId,
        input: {
          ...editValues,
          quantity: editValues.quantity !== "" ? Number(editValues.quantity) : undefined,
          perUnitCostUSD: Number(editValues.perUnitCostUSD),
          userStatus: "edited",
        },
        ...tVars,
      }).unwrap();
      setEditingId(null);
    } catch (err) {
      console.error("Edit failed:", err);
    }
  };

  const handleAddRow = async () => {
    if (!newRow.supplierName || !newRow.materialDescription || !newRow.perUnitCostUSD) return;
    try {
      await addComponent({
        caseId,
        input: {
          supplierName: newRow.supplierName,
          materialDescription: newRow.materialDescription,
          perUnitCostUSD: Number(newRow.perUnitCostUSD),
        },
        ...tVars,
      }).unwrap();
      setNewRow({ supplierName: "", materialDescription: "", perUnitCostUSD: "" });
      setShowAddRow(false);
    } catch (err) {
      console.error("Add failed:", err);
    }
  };

  const handleRemove = async (componentId: string) => {
    try {
      await removeComponent({ caseId, componentId, ...tVars }).unwrap();
    } catch (err) {
      console.error("Remove failed:", err);
    }
  };

  if (!materialsProvided) return null;

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#f8f9fa",
          borderBottom: "1px solid #e2e5e9",
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ExtractIcon sx={{ fontSize: 18, color: tokens.brand.accent }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Assist Data Extraction
            </Typography>
            {components.length > 0 && (
              <Stack direction="row" spacing={0.5}>
                {confirmedCount > 0 && (
                  <Chip label={`${confirmedCount} confirmed`} size="small" color="success" variant="outlined" sx={{ height: 20 }} />
                )}
                {pendingCount > 0 && (
                  <Chip label={`${pendingCount} pending`} size="small" variant="outlined" sx={{ height: 20 }} />
                )}
                {rejectedCount > 0 && (
                  <Chip label={`${rejectedCount} rejected`} size="small" color="error" variant="outlined" sx={{ height: 20 }} />
                )}
              </Stack>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {!isExtracting && (
              <Button
                size="small"
                startIcon={extraction ? <RefreshIcon /> : <ExtractIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRunExtraction();
                }}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                {extraction ? "Re-extract" : "Extract"}
              </Button>
            )}
            {isExtracting && <CircularProgress size={16} />}
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </Stack>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 0 }}>
          {/* Extracting state */}
          {isExtracting && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" color={tokens.text.muted}>
                Extracting assist data from costsheet and invoices...
              </Typography>
            </Box>
          )}

          {/* Error state */}
          {extraction?.status === "FAILED" && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="error.main">
                Extraction failed: {extraction.errorMessage || "Unknown error"}
              </Typography>
            </Box>
          )}

          {/* Empty state */}
          {!extraction && !isExtracting && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color={tokens.text.muted} mb={1}>
                No extraction data yet. Run extraction to pull assist data from the costsheet.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ExtractIcon />}
                onClick={handleRunExtraction}
                sx={{ textTransform: "none" }}
              >
                Extract Assist Data
              </Button>
            </Box>
          )}

          {/* Data table */}
          {extraction?.status === "COMPLETED" && components.length > 0 && (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Supplier</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Component</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">Qty/unit</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">$/unit</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Applies To</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Invoice</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Payment</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="center">Match</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {components.map((comp: any) => {
                      const isEditing = editingId === comp.componentId;
                      const isRejected = comp.userStatus === "rejected";

                      return (
                        <TableRow
                          key={comp.componentId}
                          sx={{
                            opacity: isRejected ? 0.5 : 1,
                            textDecoration: isRejected ? "line-through" : "none",
                            "&:hover": { bgcolor: "#f5f5f5" },
                          }}
                        >
                          <TableCell sx={{ fontSize: 11, maxWidth: 130 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editValues.supplierName}
                                onChange={(e) => setEditValues({ ...editValues, supplierName: e.target.value })}
                                sx={{ "& input": { fontSize: 11, py: 0.5 } }}
                              />
                            ) : (
                              <Typography variant="caption" fontWeight={500}>{comp.supplierName}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, maxWidth: 150 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editValues.materialDescription}
                                onChange={(e) => setEditValues({ ...editValues, materialDescription: e.target.value })}
                                sx={{ "& input": { fontSize: 11, py: 0.5 } }}
                              />
                            ) : (
                              <Tooltip title={comp.notes || comp.matchNotes || ""}>
                                <Typography variant="caption">{comp.materialDescription}</Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 10, color: tokens.text.muted }}>
                            {comp.materialType || "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editValues.quantity}
                                onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                                sx={{ width: 60, "& input": { fontSize: 11, py: 0.5, textAlign: "right" } }}
                              />
                            ) : (
                              comp.quantity != null
                                ? `${comp.quantity} ${comp.quantityUnit || ""}`
                                : "—"
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editValues.perUnitCostUSD}
                                onChange={(e) => setEditValues({ ...editValues, perUnitCostUSD: e.target.value })}
                                sx={{ width: 65, "& input": { fontSize: 11, py: 0.5, textAlign: "right" } }}
                              />
                            ) : (
                              comp.perUnitCostUSD.toFixed(2)
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 10 }}>
                            {comp.appliesTo ? (
                              <Typography variant="caption" sx={{ fontSize: 10 }}>{comp.appliesTo}</Typography>
                            ) : "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 10, maxWidth: 160 }}>
                            {comp.invoiceNumber ? (
                              <Stack spacing={0}>
                                <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 500 }}>
                                  {comp.invoiceNumber}
                                </Typography>
                                {(comp.invoiceNetAmount || comp.invoiceGrossAmount) && (
                                  <Typography variant="caption" sx={{ fontSize: 9, color: tokens.text.muted }}>
                                    {comp.invoiceNetAmount ? `Net ${comp.invoiceNetAmount.toLocaleString()}` : ""}
                                    {comp.invoiceGrossAmount ? ` / Gross ${comp.invoiceGrossAmount.toLocaleString()}` : ""}
                                    {comp.invoiceCurrency ? ` ${comp.invoiceCurrency}` : ""}
                                  </Typography>
                                )}
                                {comp.notes && (
                                  <Typography variant="caption" sx={{ fontSize: 9, color: tokens.text.muted, fontStyle: "italic" }}>
                                    {comp.notes.length > 60 ? comp.notes.slice(0, 60) + "..." : comp.notes}
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="error.main" sx={{ fontSize: 10 }}>
                                No invoice found
                                {comp.matchNotes && (
                                  <Typography component="span" sx={{ display: "block", fontSize: 9, color: tokens.text.muted }}>
                                    {comp.matchNotes}
                                  </Typography>
                                )}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 10 }}>
                            {comp.paymentDate ? (
                              <Stack spacing={0}>
                                <Typography variant="caption" sx={{ fontSize: 10 }}>{comp.paymentDate}</Typography>
                                {comp.paymentAmount && (
                                  <Typography variant="caption" sx={{ fontSize: 9, color: tokens.text.muted }}>
                                    {comp.paymentAmount.toLocaleString()} {comp.invoiceCurrency || "EUR"}
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color={tokens.text.muted} sx={{ fontSize: 10 }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={comp.confidence}
                              size="small"
                              color={CONFIDENCE_COLORS[comp.confidence] || "default"}
                              variant="outlined"
                              sx={{ height: 18, fontSize: 9 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {isEditing ? (
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Button size="small" onClick={() => handleSaveEdit(comp.componentId)} sx={{ fontSize: 10, minWidth: 0, textTransform: "none" }}>
                                  Save
                                </Button>
                                <Button size="small" onClick={() => setEditingId(null)} sx={{ fontSize: 10, minWidth: 0, textTransform: "none" }}>
                                  Cancel
                                </Button>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={0} justifyContent="center">
                                <Tooltip title="Confirm">
                                  <IconButton size="small" onClick={() => handleStatusUpdate(comp.componentId, "confirmed")} sx={{ color: "success.main" }}>
                                    <ConfirmIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton size="small" onClick={() => handleStatusUpdate(comp.componentId, "rejected")} sx={{ color: "error.main" }}>
                                    <RejectIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleStartEdit(comp)}>
                                    <EditIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton size="small" onClick={() => handleRemove(comp.componentId)} sx={{ color: tokens.text.muted }}>
                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Add row */}
                    {showAddRow && (
                      <TableRow>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Supplier"
                            value={newRow.supplierName}
                            onChange={(e) => setNewRow({ ...newRow, supplierName: e.target.value })}
                            sx={{ "& input": { fontSize: 12, py: 0.5 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Material"
                            value={newRow.materialDescription}
                            onChange={(e) => setNewRow({ ...newRow, materialDescription: e.target.value })}
                            sx={{ "& input": { fontSize: 12, py: 0.5 } }}
                          />
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="$/unit"
                            value={newRow.perUnitCostUSD}
                            onChange={(e) => setNewRow({ ...newRow, perUnitCostUSD: e.target.value })}
                            sx={{ width: 70, "& input": { fontSize: 12, py: 0.5, textAlign: "right" } }}
                          />
                        </TableCell>
                        <TableCell colSpan={3} />
                        <TableCell colSpan={2} align="center">
                          <Button size="small" onClick={handleAddRow} sx={{ fontSize: 11, textTransform: "none" }}>Add</Button>
                          <Button size="small" onClick={() => setShowAddRow(false)} sx={{ fontSize: 11, textTransform: "none" }}>Cancel</Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary footer — cost breakdown */}
              {summary && (
                <Box sx={{ px: 2, py: 1.5, borderTop: "2px solid #e2e5e9", bgcolor: "#fafafa" }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={600}>Materials Total</Typography>
                      <Typography variant="caption" fontWeight={600}>{summary.materialsTotal.toFixed(2)} {summary.currency}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={tokens.text.muted}>+ Sewing / CM</Typography>
                      <Typography variant="caption">{summary.sewingCost.toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={tokens.text.muted}>+ Pattern / Assists OH</Typography>
                      <Typography variant="caption">{summary.assistOverhead.toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color={tokens.text.muted}>+ Transport</Typography>
                      <Typography variant="caption">{summary.transportCost.toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.5, borderTop: "1px solid #ddd" }}>
                      <Typography variant="body2" fontWeight={700}>First Sale Price (per unit)</Typography>
                      <Typography variant="body2" fontWeight={700}>{summary.firstSalePrice.toFixed(2)} {summary.currency}</Typography>
                    </Stack>
                  </Stack>
                </Box>
              )}

              {/* ─── Savings Derivation Table ───────────────────────── */}
              <SavingsDerivationTable
                summary={summary}
                middlemanValue={middlemanValue}
                dutyRate={dutyRate}
                htsCode={htsCode}
              />

              {/* Add button */}
              {!showAddRow && (
                <Box sx={{ px: 2, py: 1, borderTop: "1px solid #f0f0f0" }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddRow(true)}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Add Component
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};
